import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import Rating from './Rating';
import TaskChecklist from '../../Common/TaskChecklist';
import { ChecklistDisplay } from '../../Common/TaskChecklist';
import 'react-datepicker/dist/react-datepicker.css';
import './ActiveJobs.css';
import '../CleanerDashboard/AvailableTasks.css';

// ── Mapping helpers (mirrors AvailableTasks.js) ──
const SERVICE_TYPES = { home: 'Home Cleaning', commercial: 'Commercial Cleaning', moveinout: 'Move-in/Move-out' };
const CLEANING_CATEGORIES = { standard: 'Standard Cleaning', main: 'Main Cleaning', adhoc: 'Ad hoc Cleaning' };
const KNOWN_AS_NEEDED = ['wipingFrames', 'limeShower', 'vacuumFurniture', 'cobwebs'];
const KNOWN_MAIN_EXTRAS = [
  'wipingFrames', 'wipingWindowEdges', 'wipingCeilingEdges', 'windowCleaning',
  'blindsCleaned', 'furnitureVacuumed', 'behindFurniture', 'cleaningWallsCeilings',
  'underCarpets', 'mirrorsGlass', 'ceilingLights', 'closedSpaces',
  'insideCupboards', 'woodenFloors', 'ovenFridge',
];
const KNOWN_ADHOC = ['standardWithout', 'vacuumAndFloor', 'bathroomAndFloor', 'largeHome'];
const KNOWN_EXTRA_TARGETED = ['animalHair', 'smoking'];
const EQUIPMENT_PREFIX = 'equipment:';
const EQUIPMENT_KEYS = ['cleaningAgents', 'cloth', 'vacuumCleaner', 'mop', 'specialProducts'];
const ALL_KNOWN = new Set([...KNOWN_AS_NEEDED, ...KNOWN_MAIN_EXTRAS, ...KNOWN_ADHOC, ...KNOWN_EXTRA_TARGETED]);

const mapServiceType = (cleaningType) => {
  if (cleaningType === 'residential') return 'home';
  if (cleaningType === 'move') return 'moveinout';
  if (cleaningType === 'deep') return 'home';
  return cleaningType;
};
const mapCleaningCategory = (cleaningType) => (cleaningType === 'deep' ? 'main' : 'standard');

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripTrailingCityZip = (value) => {
  const address = String(value || '').trim();
  if (!address) return '';

  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) return address;

  const zipLike = /^\d{3,10}(?:[-\s]\d{2,6})?$/;
  const hasLetters = /[a-zA-Z]/;
  const looksLikeCityZipCombined = /[a-zA-Z].*\d{3,}|\d{3,}.*[a-zA-Z]/;

  const last = parts[parts.length - 1];
  const prev = parts[parts.length - 2] || '';

  if (zipLike.test(last) && hasLetters.test(prev)) {
    return parts.slice(0, -2).join(', ').trim();
  }

  if (zipLike.test(last)) {
    return parts.slice(0, -1).join(', ').trim();
  }

  if (looksLikeCityZipCombined.test(last)) {
    return parts.slice(0, -1).join(', ').trim();
  }

  return address;
};

const getListAddress = (task) => {
  const rawAddress = String(task?.address || '').trim();
  if (!rawAddress) return '—';

  let cleanedAddress = rawAddress;
  const city = String(task?.client?.city || '').trim();
  const zipCode = String(task?.client?.zipCode || '').trim();

  if (zipCode) {
    cleanedAddress = cleanedAddress.replace(new RegExp(`,?\\s*${escapeRegExp(zipCode)}\\s*$`, 'i'), '');
  }
  if (city) {
    cleanedAddress = cleanedAddress.replace(new RegExp(`,?\\s*${escapeRegExp(city)}\\s*$`, 'i'), '');
  }

  cleanedAddress = stripTrailingCityZip(cleanedAddress);
  cleanedAddress = cleanedAddress.replace(/[\s,]+$/, '').trim();
  return cleanedAddress || rawAddress;
};

const ActiveJobs = ({ userId, statusFilter }) => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [activeTasks, setActiveTasks] = useState([]);
  const [recurringSiblings, setRecurringSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [cleanerReviewsMeta, setCleanerReviewsMeta] = useState({ averageRating: null, count: 0, reviews: [] });
  const [loadingCleanerInfo, setLoadingCleanerInfo] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editFormData, setEditFormData] = useState({
    address: '',
    date: new Date(),
    time: '',
    hours: 1,
    comments: '',
    cleaningType: 'residential'
  });
  const [editChecklist, setEditChecklist] = useState([]);

  const getTaskIdNumber = (taskId) => {
    if (!taskId) return '';
    if (typeof taskId === 'number') return taskId.toString();
    const idStr = String(taskId);
    if (idStr.length === 24) return idStr.slice(-6);
    const match = idStr.match(/-(\d+)$/);
    return match ? match[1] : idStr;
  };

  useEffect(() => {
    fetchActiveTasks(true);
    const interval = setInterval(() => fetchActiveTasks(false), 30000);
    return () => clearInterval(interval);
  }, [userId]);

  const groupRecurringTasks = (items) => {
    const groups = new Map();
    const singles = [];

    (Array.isArray(items) ? items : []).forEach((task) => {
      const groupId = task.recurrenceGroupId;
      if (!groupId) {
        singles.push({ ...task, recurrenceCount: 1, isRecurring: false });
        return;
      }

      const key = String(groupId);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, { ...task, isRecurring: true, recurrenceCount: 1, _count: 1 });
        return;
      }

      existing._count += 1;
      existing.recurrenceCount = Math.max(existing.recurrenceCount || 1, existing._count);

      const existingDate = existing.date ? new Date(existing.date) : null;
      const candidateDate = task.date ? new Date(task.date) : null;
      if (candidateDate && (!existingDate || candidateDate < existingDate)) {
        Object.assign(existing, {
          ...task,
          isRecurring: true,
          recurrenceCount: existing.recurrenceCount,
          _count: existing._count,
        });
      }
    });

    const grouped = Array.from(groups.values()).map((task) => {
      const normalized = { ...task };
      delete normalized._count;
      return normalized;
    });

    return [...grouped, ...singles];
  };

  const fetchActiveTasks = async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);
      let tasks = [];
      const getScheduleTimestamp = (task) => {
        if (!task?.date) return 0;
        const dateOnly = String(task.date).split('T')[0];
        const timeValue = task.time || '00:00';
        const timestamp = new Date(`${dateOnly}T${timeValue}`).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
      };

      if (statusFilter === 'pending') {
        const response = await api.get('/tasks', { params: { status: 'pending' } });
        tasks = response.data.data;
      } else if (statusFilter === 'approved') {
        const assignedResponse = await api.get('/tasks/assigned');
        const acceptedTasks = assignedResponse.data.data;
        tasks = [...acceptedTasks];
      } else {
        const [pendingRes, assignedRes, completedRes] = await Promise.all([
          api.get('/tasks', { params: { status: 'pending' } }),
          api.get('/tasks/assigned'),
          api.get('/tasks/completed')
        ]);
        const pendingTasks = pendingRes.data.data;
        const assignedTasks = assignedRes.data.data;
        const completedTasks = completedRes.data.data.filter(t => !t.rating);
        tasks = [...pendingTasks, ...assignedTasks, ...completedTasks];
      }

      if (startDate && endDate) {
        tasks = tasks.filter(task => {
          const taskDate = new Date(task.date);
          return taskDate >= startDate && taskDate <= endDate;
        });
      }

      // For approved tab, show all recurring tasks separately with individual dates
      // For pending tab, group recurring tasks into one entry
      let displayTasks;
      if (statusFilter === 'approved') {
        displayTasks = (tasks || []).map(task => ({
          ...task,
          isRecurring: Boolean(task.recurrenceGroupId),
          recurrenceCount: 1,
        }));
      } else {
        displayTasks = groupRecurringTasks(tasks);
      }
      displayTasks.sort((a, b) => getScheduleTimestamp(a) - getScheduleTimestamp(b));
      setActiveTasks(displayTasks);
    } catch (error) {
      console.error('Error fetching active tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveTasks(true);
  }, [startDate, endDate, statusFilter]);

  // Fetch recurring siblings when a recurring task detail is opened
  useEffect(() => {
    const groupId = selectedTask?.recurrenceGroupId;
    if (!groupId) { setRecurringSiblings([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks', { params: {} });
        if (cancelled) return;
        const all = Array.isArray(res.data?.data) ? res.data.data : [];
        const siblings = all
          .filter(t => String(t.recurrenceGroupId) === String(groupId))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setRecurringSiblings(siblings);
      } catch { if (!cancelled) setRecurringSiblings([]); }
    })();
    return () => { cancelled = true; };
  }, [selectedTask?.recurrenceGroupId]);

  const handleRatingSubmit = async (taskId, rating, comment) => {
    try {
      await api.post(`/tasks/${taskId}/rate`, { rating, ratingComment: comment });
      fetchActiveTasks();
    } catch (error) {
      console.error('Error submitting rating:', error);
      showToast(error.response?.data?.message || t('client.errorRating', 'Error submitting rating. Please try again.'), 'error');
    }
  };

  const handleEditClick = (task) => {
    setSelectedTask(null);
    setEditingTask(task);
    setEditFormData({
      address: task.address || '',
      date: new Date(task.date),
      time: task.time || '',
      hours: task.hours || 1,
      comments: task.comments || '',
      cleaningType: task.cleaningType || 'residential'
    });
    setEditChecklist(Array.isArray(task.checklist) ? task.checklist : []);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditDateChange = (date) => {
    setEditFormData(prev => ({ ...prev, date }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/tasks/${editingTask._id || editingTask.id}`, {
        address: editFormData.address,
        date: editFormData.date.toISOString(),
        time: editFormData.time,
        hours: parseInt(editFormData.hours),
        comments: editFormData.comments,
        cleaningType: editFormData.cleaningType,
        checklist: editChecklist
      });
      setEditingTask(null);
      fetchActiveTasks();
      showToast(t('client.orderUpdated', 'Order updated successfully!'), 'success');
    } catch (error) {
      console.error('Error updating order:', error);
      showToast(error.response?.data?.message || t('client.errorUpdating', 'Error updating order. Please try again.'), 'error');
    }
  };

  const handleCancelOrder = async (task) => {
    if (!window.confirm(t('client.confirmCancel', 'Are you sure you want to cancel this order?'))) return;
    try {
      await api.post(`/tasks/${task._id || task.id}/cancel`);
      setSelectedTask(null);
      fetchActiveTasks();
      showToast(t('client.orderCancelled', 'Order cancelled successfully!'), 'success');
    } catch (error) {
      console.error('Error cancelling order:', error);
      showToast(error.response?.data?.message || t('client.errorCancelling', 'Error cancelling order. Please try again.'), 'error');
    }
  };

  const renderStars = (value) => {
    const rating = Math.max(0, Math.min(5, Number(value) || 0));
    const full = Math.round(rating);
    return `${'★'.repeat(full)}${'☆'.repeat(Math.max(0, 5 - full))}`;
  };

  const resolveCleanerPhotoUrl = (cleaner) => {
    const photo = cleaner?.photo;
    if (!photo || typeof photo !== 'string') return null;
    if (photo.startsWith('data:image') || photo.startsWith('http://') || photo.startsWith('https://')) return photo;
    if (/^[A-Za-z0-9+/=\n\r]+$/.test(photo) && photo.length > 100) {
      return `data:image/jpeg;base64,${photo.replace(/\s/g, '')}`;
    }
    if (photo.startsWith('/')) {
      const apiBase = String(api.defaults.baseURL || '').replace(/\/api\/?$/, '');
      return `${apiBase}${photo}`;
    }
    if (photo.startsWith('uploads/')) {
      const apiBase = String(api.defaults.baseURL || '').replace(/\/api\/?$/, '');
      return `${apiBase}/${photo}`;
    }
    return null;
  };

  const openTaskDetails = async (task) => {
    const taskId = task?._id || task?.id;
    const fallbackCleanerId = task?.cleaner?.id;
    if (!taskId) {
      setSelectedTask(task);
      return;
    }

    setLoadingCleanerInfo(true);
    setSelectedTask(task);
    setCleanerReviewsMeta({ averageRating: null, count: 0, reviews: [] });

    try {
      const detailRes = await api.get(`/tasks/${taskId}`);
      const detailedTask = detailRes?.data?.success ? detailRes.data.data : task;
      setSelectedTask({
        ...detailedTask,
        isRecurring: detailedTask.isRecurring || Boolean(detailedTask.recurrenceGroupId) || task.isRecurring,
        recurrenceCount: detailedTask.recurrenceCount || task.recurrenceCount,
      });

      const cleanerId = detailedTask?.cleaner?.id || fallbackCleanerId;
      if (cleanerId) {
        const reviewsRes = await api.get(`/tasks/cleaners/${cleanerId}/reviews`);
        if (reviewsRes?.data?.success) {
          setCleanerReviewsMeta(reviewsRes.data.data || { averageRating: null, count: 0, reviews: [] });
        }
      }
    } catch {
      if (fallbackCleanerId) {
        try {
          const reviewsRes = await api.get(`/tasks/cleaners/${fallbackCleanerId}/reviews`);
          if (reviewsRes?.data?.success) {
            setCleanerReviewsMeta(reviewsRes.data.data || { averageRating: null, count: 0, reviews: [] });
          }
        } catch {
          // Keep modal usable even if additional cleaner data cannot be loaded.
        }
      }
    } finally {
      setLoadingCleanerInfo(false);
    }
  };

  if (loading) {
    return <div className="aj-loading">{t('common.loading', 'Loading...')}</div>;
  }

  const getTitle = () => {
    if (statusFilter === 'pending') return t('client.pendingRequests', 'Pending Requests');
    if (statusFilter === 'approved') return t('client.approvedJobs', 'Approved Jobs');
    return t('client.pendingRequests', 'Pending Requests');
  };

  const getDescription = () => {
    if (statusFilter === 'pending') return t('client.yourPendingRequests', 'Your cleaning requests waiting for approval');
    if (statusFilter === 'approved') return t('client.yourApprovedJobs', 'Your approved cleaning jobs');
    return t('client.yourPendingRequests', 'Your cleaning requests waiting for approval');
  };

  return (
    <div className="aj-container">
      <div className="aj-header">
        <div>
          <h2>{getTitle()}</h2>
          <p className="aj-description">{getDescription()}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="aj-filters">
        <div className="aj-filter-group">
          <label>{t('common.from', 'From')}</label>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText={t('common.startDate', 'Start date')}
            dateFormat="dd,MM,yyyy"
            className="aj-filter-input"
          />
        </div>
        <div className="aj-filter-group">
          <label>{t('common.to', 'To')}</label>
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            placeholderText={t('common.endDate', 'End date')}
            dateFormat="dd,MM,yyyy"
            className="aj-filter-input"
          />
        </div>
        {(startDate || endDate) && (
          <button
            className="aj-btn aj-btn-secondary aj-btn-sm"
            onClick={() => { setStartDate(null); setEndDate(null); }}
          >
            {t('common.clearFilter', 'Clear Filter')}
          </button>
        )}
      </div>

      {activeTasks.length === 0 ? (
        <p className="aj-no-tasks">
          {statusFilter === 'pending' ? t('client.noPendingRequests', 'No pending requests.') :
           statusFilter === 'approved' ? t('client.noActiveJobs', 'No active jobs at the moment.') :
           t('client.noActiveJobs', 'No active jobs at the moment.')}
        </p>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="aj-table-wrapper">
            <table className="aj-table">
              <thead>
                <tr>
                  <th>{t('admin.taskId', 'Task ID')}</th>
                  <th>{t('auth.address', 'Address')}</th>
                  <th>{t('client.date', 'Date')}</th>
                  <th>{t('client.time', 'Time')}</th>
                  <th>{t('client.hours', 'Hours')}</th>
                  <th>{t('admin.type', 'Type')}</th>
                  <th>{t('admin.frequency', 'Frequency')}</th>
                  <th>{t('status.status', 'Status')}</th>
                  <th>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {activeTasks.map(task => {
                  const taskId = task._id || task.id;
                  return (
                    <tr key={taskId}>
                      <td>#{getTaskIdNumber(taskId)}</td>
                      <td>{getListAddress(task)}</td>
                      <td>{new Date(task.date).toLocaleDateString('en-GB')}</td>
                      <td>{task.time}</td>
                      <td>{task.hours}</td>
                      <td>{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</td>
                      <td>
                        {task.isRecurring ? (
                          <span className="aj-recurring-badge">
                            🔄 Every {task.recurrenceEvery ?? 1} week(s){task.recurrenceCount > 1 ? ` (${task.recurrenceCount} tasks)` : ''}
                          </span>
                        ) : (
                          <span>Once</span>
                        )}
                      </td>
                      <td>
                        <span className={`aj-status aj-status-${task.status}`}>
                          {task.status}
                        </span>
                      </td>
                      <td>
                        <div className="aj-action-buttons">
                          <button
                            className="aj-btn aj-btn-primary aj-btn-sm"
                            onClick={() => openTaskDetails(task)}
                          >
                            {t('common.details', 'Details')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="aj-cards">
            {activeTasks.map(task => {
              const taskId = task._id || task.id;
              return (
                <div key={taskId} className="aj-card">
                  <div className="aj-card-header">
                    <span className="aj-card-id">#{getTaskIdNumber(taskId)}</span>
                    <span className={`aj-status aj-status-${task.status}`}>
                      {task.status}
                    </span>
                  </div>
                  <div className="aj-card-details">
                    <div className="aj-card-row">
                      <span className="aj-card-label">{t('auth.address', 'Address')}:</span>
                      <span className="aj-card-value">{getListAddress(task)}</span>
                    </div>
                    <div className="aj-card-row">
                      <span className="aj-card-label">{t('client.date', 'Date')}:</span>
                      <span className="aj-card-value">{new Date(task.date).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div className="aj-card-row">
                      <span className="aj-card-label">{t('client.time', 'Time')}:</span>
                      <span className="aj-card-value">{task.time}</span>
                    </div>
                    <div className="aj-card-row">
                      <span className="aj-card-label">{t('client.hours', 'Hours')}:</span>
                      <span className="aj-card-value">{task.hours}</span>
                    </div>
                    <div className="aj-card-row">
                      <span className="aj-card-label">{t('admin.serviceType', 'Service Type')}:</span>
                      <span className="aj-card-value">{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</span>
                    </div>
                    {task.isRecurring ? (
                      <div className="aj-card-row">
                        <span className="aj-card-label">{t('admin.frequency', 'Frequency')}:</span>
                        <span className="aj-card-value aj-recurring-badge">
                          🔄 Every {task.recurrenceEvery ?? 1} week(s){task.recurrenceCount > 1 ? ` (${task.recurrenceCount} tasks)` : ''}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div className="aj-card-actions">
                    <button
                      className="aj-btn aj-btn-primary aj-btn-sm"
                      onClick={() => openTaskDetails(task)}
                    >
                      {t('common.details', 'Details')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Details Modal */}
      {selectedTask && (
        <div className="aj-modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="aj-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="aj-modal-header">
              <h3>{t('common.details', 'Details')} - #{getTaskIdNumber(selectedTask._id || selectedTask.id)}</h3>
              <button className="aj-close-btn" onClick={() => setSelectedTask(null)}>&times;</button>
            </div>
            <div className="aj-modal-body">
              {/* Status */}
              <div className="at-detail-status">
                <span className={`aj-status aj-status-${selectedTask.status}`}>{selectedTask.status}</span>
              </div>

              {/* Service Type */}
              <div className="at-detail-section">
                <h4>{t('newOrder.serviceType', 'Service Type')}</h4>
                <p>{SERVICE_TYPES[mapServiceType(selectedTask.cleaningType)] || selectedTask.cleaningType}</p>
              </div>

              {/* Assigned Cleaner */}
              {(selectedTask.status === 'assigned' || selectedTask.status === 'accepted') && selectedTask.cleaner ? (
                <div className="at-detail-section">
                  <h4>{t('orders.cleanerInfo', 'Assigned Cleaner')}</h4>
                  {loadingCleanerInfo ? <p>{t('common.loading', 'Loading...')}</p> : null}

                  <div className="aj-cleaner-profile">
                    {resolveCleanerPhotoUrl(selectedTask.cleaner) ? (
                      <img
                        src={resolveCleanerPhotoUrl(selectedTask.cleaner)}
                        alt={selectedTask.cleaner.name || t('client.cleaner', 'Cleaner')}
                        className="aj-cleaner-photo"
                      />
                    ) : (
                      <div className="aj-cleaner-avatar-fallback">
                        {(selectedTask.cleaner.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="aj-cleaner-meta">
                      <div className="aj-cleaner-name">{selectedTask.cleaner.name || t('client.cleaner', 'Cleaner')}</div>
                      {Number.isFinite(Number(cleanerReviewsMeta?.averageRating)) ? (
                        <div className="aj-cleaner-rating-summary">
                          {renderStars(cleanerReviewsMeta.averageRating)} {Number(cleanerReviewsMeta.averageRating).toFixed(1)} ({cleanerReviewsMeta.count})
                        </div>
                      ) : (
                        <div className="aj-cleaner-rating-summary">{t('orders.noReviews', 'No reviews yet')}</div>
                      )}
                    </div>
                  </div>

                  {Array.isArray(cleanerReviewsMeta?.reviews) && cleanerReviewsMeta.reviews.length > 0 ? (
                    <div className="aj-cleaner-reviews-list">
                      {cleanerReviewsMeta.reviews.slice(0, 3).map((review) => (
                        <div key={review.id} className="aj-cleaner-review-item">
                          <div className="aj-cleaner-review-rating">{renderStars(review.rating)} {Number(review.rating).toFixed(1)}</div>
                          {review.ratingComment ? <p className="aj-cleaner-review-comment">{review.ratingComment}</p> : null}
                          <div className="aj-cleaner-review-meta">
                            {(review.client?.name || t('orders.client', 'Client'))} • {new Date(review.completedAt || review.date).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Recurring Information */}
              {(selectedTask.isRecurring || selectedTask.recurrenceGroupId) && (
                <div className="at-detail-section">
                  <h4>{t('orders.recurringInfo', 'Recurring Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🔄</span>
                    <span>Every {selectedTask.recurrenceEvery ?? 1} week(s){selectedTask.recurrenceCount > 1 ? ` (${selectedTask.recurrenceCount} tasks)` : ''}</span>
                  </div>
                  {Array.isArray(selectedTask.recurrenceDays) && selectedTask.recurrenceDays.length > 0 && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📅</span>
                      <span>{selectedTask.recurrenceDays.map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d] || d).join(', ')}</span>
                    </div>
                  )}
                  {selectedTask.recurrenceUntil && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">🏁</span>
                      <span>Until {selectedTask.recurrenceUntil}</span>
                    </div>
                  )}
                  {recurringSiblings.length > 1 && (() => {
                    const sibCleanerCounts = new Map();
                    recurringSiblings.forEach(s => {
                      const cId = String(s.cleaner?.id || s.cleanerId || '');
                      if (cId) sibCleanerCounts.set(cId, (sibCleanerCounts.get(cId) || 0) + 1);
                    });
                    let sibPrimaryCleanerId = '', sibMaxCount = 0;
                    for (const [cId, count] of sibCleanerCounts) {
                      if (count > sibMaxCount) { sibMaxCount = count; sibPrimaryCleanerId = cId; }
                    }

                    return (
                      <div style={{ marginTop: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                        <strong style={{ fontSize: '0.9em' }}>{t('orders.upcomingDates', 'Scheduled Dates')}</strong>
                        <div style={{ marginTop: '6px' }}>
                          {recurringSiblings.map((s, idx) => {
                            const sDate = s.date ? new Date(s.date) : null;
                            if (sDate) sDate.setHours(0, 0, 0, 0);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const isPast = sDate && sDate < today;
                            const isCurrent = String(s.id) === String(selectedTask.id);
                            const statusLabel = s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : '';
                            const sCId = String(s.cleaner?.id || s.cleanerId || '');
                            const isSibSubstitute = sibPrimaryCleanerId && sCId && sCId !== sibPrimaryCleanerId;
                            return (
                              <div key={s.id || idx} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                backgroundColor: isCurrent ? '#e6f7ff' : isSibSubstitute ? '#FFFBEB' : 'transparent',
                                opacity: isPast ? 0.5 : 1,
                              }}>
                                <span>{s.status === 'completed' ? '✅' : isPast ? '⭕' : '🔵'}</span>
                                <span style={{ flex: 1, textDecoration: isPast ? 'line-through' : 'none', color: isPast ? '#a0aec0' : '#2d3748' }}>
                                  {sDate ? sDate.toLocaleDateString('en-GB') : '—'}
                                  {isSibSubstitute && s.cleaner?.name && (
                                    <span style={{ marginLeft: '6px', fontSize: '0.8em', color: '#92400E', fontWeight: 600 }}>
                                      ({s.cleaner.name} — Substitute)
                                    </span>
                                  )}
                                </span>
                                <span style={{ fontSize: '0.8em', fontWeight: 600, textTransform: 'uppercase', color: s.status === 'completed' ? '#38a169' : s.status === 'accepted' ? '#2c7a7b' : s.status === 'assigned' ? '#2c7a7b' : '#ed8936' }}>
                                  {statusLabel}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Title */}
              {selectedTask.title && (
                <div className="at-detail-section">
                  <h4>{t('orders.title', 'Title')}</h4>
                  <p>{selectedTask.title}</p>
                </div>
              )}

              {/* Booking Details */}
              <div className="at-detail-section">
                <h4>{t('newOrder.bookingDetails', 'Booking Details')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📍</span>
                  <span>{selectedTask.address || '—'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📅</span>
                  <span>{new Date(selectedTask.date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🕐</span>
                  <span>{selectedTask.allDay ? t('orders.allDay', 'All Day') : (selectedTask.time || '—')}{selectedTask.endTime ? ` – ${selectedTask.endTime}` : ''}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏳</span>
                  <span>{selectedTask.hours || '–'} {t('admin.hours', 'hours')}{selectedTask.actualHours ? ` (${t('cleaner.actualHours', 'Actual')}: ${selectedTask.actualHours})` : ''}</span>
                </div>
              </div>

              {/* Dynamic checklist-derived sections */}
              {(() => {
                const checklist = Array.isArray(selectedTask.checklist) ? selectedTask.checklist : [];
                const asNeeded = checklist.filter(i => KNOWN_AS_NEEDED.includes(i));
                const mainExtras = checklist.filter(i => KNOWN_MAIN_EXTRAS.includes(i));
                const adhoc = checklist.filter(i => KNOWN_ADHOC.includes(i));
                const equipmentSelected = EQUIPMENT_KEYS.filter(k => checklist.includes(`${EQUIPMENT_PREFIX}${k}`));
                const extraTargeted = [];
                if (checklist.includes('animalHair')) extraTargeted.push(t('newOrder.animalHair', 'Animal hair'));
                if (checklist.includes('smoking')) extraTargeted.push(t('newOrder.smoking', 'Smoking'));
                const remaining = checklist.filter(item => typeof item === 'string' && !ALL_KNOWN.has(item) && !item.startsWith(EQUIPMENT_PREFIX));

                const equipmentLabels = {
                  cleaningAgents: t('newOrder.cleaningAgents', 'Cleaning agents'),
                  cloth: t('newOrder.cloth', 'Cloth'),
                  vacuumCleaner: t('newOrder.vacuumCleaner', 'Vacuum cleaner'),
                  mop: t('newOrder.mop', 'Mop'),
                  specialProducts: t('newOrder.specialProducts', 'Special products'),
                };

                const renderChecklistSection = (items, title) => items.length > 0 ? (
                  <div className="at-detail-section" key={title}>
                    <h4>{title}</h4>
                    <ul className="at-detail-list">
                      {items.map((item, i) => <li key={i}>✅ {item}</li>)}
                    </ul>
                  </div>
                ) : null;

                return (
                  <>
                    {renderChecklistSection(asNeeded, t('newOrder.asNeeded', 'As Needed'))}
                    {renderChecklistSection(mainExtras, t('newOrder.mainCleaningExtras', 'Main Cleaning Extras'))}
                    {renderChecklistSection(adhoc, t('newOrder.adhocOptions', 'Ad hoc Options'))}
                    {equipmentSelected.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('newOrder.equipment', 'Equipment')}</h4>
                        <ul className="at-detail-list">
                          {equipmentSelected.map((k) => <li key={k}>✅ {equipmentLabels[k] || k}</li>)}
                        </ul>
                      </div>
                    )}
                    {renderChecklistSection(extraTargeted, t('newOrder.extraTargeted', 'Extra Targeted'))}
                    {remaining.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('client.whatNeedsToBeDone', 'What needs to be done')}</h4>
                        <ChecklistDisplay items={remaining} completed={selectedTask.status === 'completed' ? selectedTask.checklistCompleted : undefined} />
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Comments */}
              {selectedTask.comments && (
                <div className="at-detail-section">
                  <h4>{t('newOrder.additionalComments', 'Comments')}</h4>
                  <div className="at-detail-comments">{selectedTask.comments}</div>
                </div>
              )}

              {/* Rating section for completed tasks */}
              {selectedTask.status === 'completed' && selectedTask.cleaner && (
                <div className="at-detail-section">
                  {selectedTask.rating ? (
                    <div className="aj-existing-rating">
                      <h4>{t('client.yourRating', 'Your Rating')}</h4>
                      <div className="aj-rating-display">
                        <span className="aj-rating-stars">
                          {'★'.repeat(selectedTask.rating)}{'☆'.repeat(5 - selectedTask.rating)}
                        </span>
                        <span className="aj-rating-value">{selectedTask.rating} / 5</span>
                      </div>
                      {selectedTask.ratingComment && (
                        <p className="aj-rating-comment">{selectedTask.ratingComment}</p>
                      )}
                    </div>
                  ) : selectedTask.canRate ? (
                    <div className="aj-rating-prompt">
                      <h4>{t('client.rateService', 'Rate Your Cleaning Service')}</h4>
                      <Rating
                        taskId={selectedTask._id || selectedTask.id}
                        cleanerName={selectedTask.cleaner.name}
                        onSubmit={handleRatingSubmit}
                      />
                    </div>
                  ) : selectedTask.ratingBlockedReason ? (
                    <div className="aj-rating-prompt">
                      <h4>{t('client.rateService', 'Rate Your Cleaning Service')}</h4>
                      <p>{selectedTask.ratingBlockedReason}</p>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Order Date */}
              {selectedTask.createdAt && (
                <div className="at-detail-meta">
                  <span className="at-detail-meta-label">{t('orders.orderDate', 'Order Date')}</span>
                  <span>{new Date(selectedTask.createdAt).toLocaleDateString('en-GB')}</span>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="aj-modal-actions">
              {(selectedTask.status === 'pending' || selectedTask.status === 'assigned' || selectedTask.status === 'accepted') && (
                <>
                  <button
                    className="aj-btn aj-btn-primary"
                    onClick={() => handleEditClick(selectedTask)}
                  >
                    {t('common.edit', 'Edit')}
                  </button>
                  <button
                    className="aj-btn aj-btn-danger"
                    onClick={() => handleCancelOrder(selectedTask)}
                  >
                    {t('client.cancelOrder', 'Cancel Order')}
                  </button>
                </>
              )}
              <button
                className="aj-btn aj-btn-secondary"
                onClick={() => setSelectedTask(null)}
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {editingTask && (
        <div className="aj-modal-overlay" onClick={() => setEditingTask(null)}>
          <div className="aj-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="aj-modal-header">
              <h3>{t('client.editOrder', 'Edit Order')}</h3>
              <button className="aj-close-btn" onClick={() => setEditingTask(null)}>&times;</button>
            </div>
            <form onSubmit={handleEditSubmit} className="aj-edit-form">
              <div className="aj-form-group">
                <label>{t('auth.address', 'Address')} *</label>
                <input
                  type="text"
                  name="address"
                  value={editFormData.address}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="aj-form-row">
                <div className="aj-form-group">
                  <label>{t('client.date', 'Date')} *</label>
                  <DatePicker
                    selected={editFormData.date}
                    onChange={handleEditDateChange}
                    minDate={new Date()}
                    dateFormat="dd,MM,yyyy"
                    className="aj-filter-input"
                    required
                  />
                </div>
                <div className="aj-form-group">
                  <label>{t('client.time', 'Time')} *</label>
                  <input
                    type="time"
                    name="time"
                    value={editFormData.time}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>

              <div className="aj-form-row">
                <div className="aj-form-group">
                  <label>{t('client.hours', 'Hours')} *</label>
                  <input
                    type="number"
                    name="hours"
                    value={editFormData.hours}
                    onChange={handleEditChange}
                    min="1"
                    required
                  />
                </div>
                <div className="aj-form-group">
                  <label>{t('admin.cleaningType', 'Cleaning Type')} *</label>
                  <select
                    name="cleaningType"
                    value={editFormData.cleaningType}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="residential">{t('client.residential', 'Residential')}</option>
                    <option value="commercial">{t('client.commercial', 'Commercial')}</option>
                    <option value="deep">{t('client.deep', 'Deep Cleaning')}</option>
                    <option value="move">{t('client.move', 'Move-in/Move-out')}</option>
                  </select>
                </div>
              </div>

              <TaskChecklist
                items={editChecklist}
                onChange={setEditChecklist}
                placeholder={t('client.checklistPlaceholder', 'e.g. Kitchen cleaning, Bathroom deep clean...')}
                label={t('client.whatNeedsToBeDone', 'What needs to be done')}
              />
              <div className="aj-form-group">
                <label>{t('forms.comments', 'Comments / Special Instructions')}</label>
                <textarea
                  name="comments"
                  value={editFormData.comments}
                  onChange={handleEditChange}
                  rows="4"
                  placeholder={t('forms.comments', 'Any special instructions...')}
                />
              </div>

              <div className="aj-modal-actions">
                <button type="submit" className="aj-btn aj-btn-primary">
                  {t('common.save', 'Save Changes')}
                </button>
                <button
                  type="button"
                  className="aj-btn aj-btn-secondary"
                  onClick={() => setEditingTask(null)}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveJobs;

